import { ChangeEvent, InputHTMLAttributes, KeyboardEvent } from 'react'
import { TextField, Tooltip } from '@material-ui/core'

import CurrencyTextField from 'components/CurrencyTextField'
import { NumberField } from './types'

interface NumberFieldInputProps {
  className: string
  disabled?: boolean
  field: NumberField
  placeholder?: string
  inputProps?: InputHTMLAttributes<HTMLInputElement> & Record<string, unknown>
  value: number | null
  errorMessage?: string | null
  onChange: (value: number | null) => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
}

const NumberFieldInput = ({
  className,
  disabled = false,
  field,
  inputProps,
  placeholder,
  value,
  errorMessage,
  onChange,
  onKeyDown
}: NumberFieldInputProps) => {
  const hasError = Boolean(errorMessage)
  const sharedProps = { disabled, error: hasError, placeholder, onKeyDown }

  const handleCurrencyKeyDownCapture = (event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement
    const isDigitAtEnd = /^\d$/.test(event.key) &&
      !event.altKey && !event.ctrlKey && !event.metaKey &&
      input.selectionStart === input.value.length &&
      input.selectionEnd === input.value.length
    const decimalPart = input.value.split(',')[1]
    const isBackspaceAtEnd = event.key === 'Backspace' &&
      input.selectionStart === input.value.length &&
      input.selectionEnd === input.value.length &&
      decimalPart?.length === 2

    if (isDigitAtEnd && (input.value === '' || decimalPart?.length === 2)) {
      event.preventDefault()
      event.stopPropagation()
      const sign = input.value.startsWith('-') ? -1 : 1
      const digits = input.value.replace(/\D/g, '')
      onChange(sign * Number(`${digits}${event.key}`) / 100)
    } else if (isBackspaceAtEnd) {
      event.preventDefault()
      event.stopPropagation()
      const sign = input.value.startsWith('-') ? -1 : 1
      const digits = input.value.replace(/\D/g, '').slice(0, -1)
      onChange(sign * Number(digits || 0) / 100)
    }
  }

  const input = field === 'dsr' ? (
    <TextField
      label=''
      variant='outlined'
      fullWidth
      value={value ?? ''}
      type='number'
      inputProps={{ min: 0, step: 1, style: { textAlign: 'left' }, ...inputProps }}
      onChange={event => onChange(event.target.value === '' ? null : Math.max(0, Math.floor(Number(event.target.value))))}
      {...sharedProps}
    />
  ) : (
    <CurrencyTextField
      label=''
      variant='outlined'
      fullWidth
      value={value}
      currencySymbol='R$'
      decimalCharacter=','
      decimalPlaces={2}
      decimalPlacesShownOnBlur={2}
      decimalPlacesShownOnFocus={2}
      digitGroupSeparator='.'
      emptyInputBehavior='null'
      inputProps={{ inputMode: 'decimal', style: { textAlign: 'left' }, ...inputProps }}
      onChange={(event: ChangeEvent<HTMLInputElement>, nextValue: number) => onChange(event.target.value ? nextValue : null)}
      {...sharedProps}
      onKeyDownCapture={handleCurrencyKeyDownCapture}
    />
  )

  return (
    <Tooltip title={errorMessage ?? ''} placement='top' arrow>
      <div className={className}>{input}</div>
    </Tooltip>
  )
}

export default NumberFieldInput
